import { useEffect, useState } from 'react';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { useT } from '@/lib/i18n';
import { getCertificate, type CertificateStatus } from '@/lib/auth-api';
import { usePeruriErrorTranslator } from '@/lib/peruri-errors';

export default function CertificateScreen() {
    const t = useT();
    const translateErr = usePeruriErrorTranslator();

    const [cert, setCert] = useState<CertificateStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const fetchCert = async () => {
        setLoading(true);
        setErr(null);
        try {
            const data = await getCertificate();
            setCert(data);
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCert();
    }, []);

    const message = cert
        ? translateErr('checkCertificate', cert.resultCode, undefined)
        : null;

    return (
        <MobileFrame showBack>
            <div
                className="fade-in"
                style={{ flex: 1, padding: '8px 24px 28px', display: 'flex', flexDirection: 'column' }}
            >
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Sertifikat Digital</h1>
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: 'var(--ink-500)',
                        marginBottom: 20,
                    }}
                >
                    Status terkini sertifikat elektronik Anda di Peruri CA.
                </p>

                {loading && <p style={{ color: 'var(--ink-500)' }}>Memuat…</p>}

                {cert && (
                    <>
                        <div
                            className="card"
                            style={{
                                padding: 18,
                                marginBottom: 12,
                                background:
                                    cert.statusCode === 'CERT_ACTIVE'
                                        ? 'var(--success-soft)'
                                        : cert.statusCode === 'CERT_REJECTED' ||
                                            cert.statusCode === 'CERT_EXPIRED'
                                          ? 'var(--error-soft)'
                                          : 'var(--warning-soft)',
                                borderColor:
                                    cert.statusCode === 'CERT_ACTIVE'
                                        ? 'rgba(21,128,61,0.15)'
                                        : 'rgba(180,35,24,0.15)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                <Icon
                                    name={
                                        cert.statusCode === 'CERT_ACTIVE'
                                            ? 'shieldCheck'
                                            : cert.statusCode === 'CERT_EXPIRED'
                                              ? 'clock'
                                              : 'alertTriangle'
                                    }
                                    size={22}
                                    color={
                                        cert.statusCode === 'CERT_ACTIVE'
                                            ? 'var(--success)'
                                            : cert.statusCode === 'CERT_REJECTED' ||
                                                cert.statusCode === 'CERT_EXPIRED'
                                              ? 'var(--error)'
                                              : 'var(--warning)'
                                    }
                                />
                                <div style={{ fontSize: 16, fontWeight: 700 }}>
                                    {certStatusLabel(cert.statusCode)}
                                </div>
                            </div>
                            <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink-700)' }}>
                                {message}
                            </div>
                            {cert.statusCode === 'EKYC_PENDING' && (
                                <div
                                    style={{
                                        marginTop: 10,
                                        padding: 10,
                                        background: 'rgba(180,115,0,0.08)',
                                        borderRadius: 8,
                                        fontSize: 12,
                                        lineHeight: 1.5,
                                        color: 'var(--warning)',
                                    }}
                                >
                                    <b>Catatan:</b> Tim Peruri sedang memverifikasi data Anda
                                    secara manual. Proses ini biasanya 1–3 hari kerja. Cek berkala
                                    dengan tombol <i>Refresh Status</i> di bawah.
                                </div>
                            )}
                        </div>

                        <div className="card" style={{ padding: 16, marginBottom: 14 }}>
                            <KV k="Email" v={cert.email} />
                            <KV k="Nomor HP" v={cert.phone ?? '—'} />
                            <KV
                                k="Masa Berlaku"
                                v={
                                    cert.isExpired === '0'
                                        ? 'Masih aktif (> 30 hari)'
                                        : cert.isExpired === '2'
                                          ? 'Akan expired dalam 30 hari'
                                          : cert.isExpired === '1'
                                            ? 'Sudah expired'
                                            : '—'
                                }
                                last
                            />
                        </div>

                        {err && (
                            <div
                                className="chip chip-error"
                                style={{ marginTop: 8, alignSelf: 'flex-start' }}
                            >
                                <Icon name="alert" size={14} /> {err}
                            </div>
                        )}

                        <div style={{ flex: 1 }} />

                        <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ width: '100%' }}
                            onClick={fetchCert}
                            disabled={loading}
                        >
                            <Icon name="refresh" size={16} />
                            Refresh Status
                        </button>
                    </>
                )}
            </div>
        </MobileFrame>
    );
}

function certStatusLabel(code: string): string {
    switch (code) {
        case 'CERT_ACTIVE':   return 'Aktif';
        case 'EKYC_PENDING':  return 'Menunggu Verifikasi';
        case 'REGISTERED':    return 'Terdaftar';
        case 'INITIATED':     return 'Sesi Dimulai';
        case 'CERT_REJECTED': return 'Ditolak';
        case 'CERT_EXPIRED':  return 'Kedaluwarsa';
        default:              return code;
    }
}

function KV({ k, v, last = false }: { k: string; v: string; last?: boolean }) {
    return (
        <div
            style={{
                display: 'flex',
                padding: '10px 0',
                gap: 16,
                borderBottom: last ? 'none' : '1px solid var(--ink-100)',
            }}
        >
            <div style={{ fontSize: 12, color: 'var(--ink-500)', flex: '0 0 40%' }}>{k}</div>
            <div
                style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--ink-900)',
                    flex: 1,
                    textAlign: 'right',
                    wordBreak: 'break-word',
                }}
            >
                {v}
            </div>
        </div>
    );
}
